/**
 * The PIN pad. For most of the school this is the only screen they will ever
 * interact with, and they meet it at 7:50am with a class coming down the hall.
 *
 * Everything here bends toward that moment:
 *
 *  - Keys are enormous and the feedback is instant. A flustered substitute is
 *    the design target, not a power user.
 *  - A wrong PIN shakes, clears, and says so in plain English. It never scolds
 *    and it never traps: five misses buys a thirty-second pause on THIS tablet,
 *    counted down on screen, and then the pad is live again.
 *  - Signing in shows WHO you signed in as, and which wing, before handing the
 *    room to the kiosk. A sub who typed the PIN taped inside the wrong desk
 *    drawer sees "Upper School" and catches it before the bells prove it.
 *
 * And one thing that is invisible but load-bearing: `opts.onGesture()` fires
 * SYNCHRONOUSLY inside the real pointer and click handlers of every key. On
 * Android, Web Audio only unlocks inside a genuine user-gesture turn — deferred
 * to a timer or a promise it silently does nothing. This tap on the PIN pad is
 * how the day's bells get armed. Nothing in this file may move that call.
 */

import { clearSession, runSetup, setupStatus, submitPin } from '../core/auth';
import type { Session } from '../shared/types';

export interface PinPadOptions {
  mode?: 'login' | 'admin';
  onGesture: () => void;
  onSuccess: (session: Session) => void;
}

const MIN_PIN = 4;
const MAX_PIN = 6;
/** Per spec: press and hold anywhere on the pad to reach the office door. */
const HOLD_MS = 2000;
const HOLD_HINT_MS = 650;
/** Long enough to read your own name and wing, short enough not to feel like a wait. */
const WELCOME_MS = 3000;

type Phase = 'checking' | 'offline' | 'pin' | 'setup' | 'setupDone' | 'welcome';

/**
 * What bell group this PIN just signed into. The server stamps the group's
 * display name onto the session, because the office can create and rename bell
 * groups and no table here could keep up. Falls back to the raw id rather than
 * showing nothing — a substitute needs to be able to check the room is right.
 */
function groupLabel(session: Session): string {
  return session.groupName?.trim() || session.wing;
}

interface SetupStep {
  title: string;
  hint: string;
  /** Which of the two PINs this step is collecting. */
  which: 'admin' | 'office';
  confirm: boolean;
}

const SETUP_STEPS: SetupStep[] = [
  {
    title: 'Set the admin PIN',
    hint: "Nathan's PIN. It signs in and unlocks the schedule editor.",
    which: 'admin',
    confirm: false,
  },
  { title: 'Confirm the admin PIN', hint: 'Type the same four to six digits again.', which: 'admin', confirm: true },
  { title: 'Set the office PIN', hint: 'For the front office. Must differ from the admin PIN.', which: 'office', confirm: false },
  { title: 'Confirm the office PIN', hint: 'Type the same four to six digits again.', which: 'office', confirm: true },
];

// ----------------------------------------------------------------- styling

const STYLE_ID = 'valor-pinpad-style';

/**
 * Scoped to .vpin and injected once. The palette comes from the --valor-*
 * tokens in src/styles/app.css; the literals are fallbacks so the pad is still
 * unmistakably Valor if it is ever mounted on its own.
 */
const CSS = `
.vpin {
  --pin-navy: var(--valor-navy, #040552);
  --pin-gold: var(--valor-gold, #B89758);
  --pin-parchment: var(--valor-parchment, #F5F1E8);
  --pin-ink: var(--valor-ink, #1A1A2E);
  --pin-serif: var(--valor-serif, 'EB Garamond', Georgia, 'Times New Roman', serif);
  box-sizing: border-box;
  min-height: 100dvh;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 3vh, 36px) clamp(12px, 4vw, 40px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 2vh, 22px);
  background: var(--pin-navy);
  color: var(--pin-parchment);
  font-family: var(--pin-serif);
  text-align: center;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
.vpin * { box-sizing: border-box; }
.vpin__school {
  margin: 0;
  font-size: clamp(0.72rem, 1.5vh, 0.95rem);
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--pin-gold);
}
.vpin__rule {
  width: min(420px, 74vw);
  height: 1px;
  border: 0;
  margin: clamp(6px, 1vh, 12px) 0 0;
  background: var(--pin-gold);
  opacity: 0.55;
}
.vpin__title {
  margin: 0;
  font-weight: 500;
  font-size: clamp(1.5rem, 4.4vh, 2.6rem);
  line-height: 1.15;
}
.vpin__hint {
  margin: 0;
  max-width: 34ch;
  font-size: clamp(0.95rem, 2vh, 1.15rem);
  opacity: 0.78;
}
.vpin__dots {
  display: flex;
  gap: clamp(10px, 1.8vh, 18px);
  min-height: clamp(18px, 2.6vh, 26px);
  align-items: center;
}
.vpin__dot {
  width: clamp(14px, 2vh, 20px);
  height: clamp(14px, 2vh, 20px);
  border-radius: 50%;
  border: 1px solid var(--pin-parchment);
  opacity: 0.45;
  transition: opacity 120ms ease, transform 120ms ease;
}
.vpin__dot--optional { opacity: 0.2; }
.vpin__dot--filled {
  background: var(--pin-parchment);
  opacity: 1;
  transform: scale(1.06);
}
.vpin__msg {
  margin: 0;
  min-height: clamp(1.4rem, 3.4vh, 2rem);
  max-width: 36ch;
  font-size: clamp(1rem, 2.2vh, 1.3rem);
  line-height: 1.3;
  opacity: 0.9;
}
.vpin__msg--error { color: var(--pin-gold); opacity: 1; }
.vpin__pad {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(8px, 1.5vh, 16px);
  /* Wide enough that the keys are a real target on an 11" tablet. At 430px the
     keys came out around 1.6cm on the wall — fine for a mouse, mean to a
     substitute standing at the door at 7:50 with a coat still on. */
  width: min(620px, 84vw);
}
.vpin__key {
  appearance: none;
  -webkit-appearance: none;
  min-height: clamp(58px, 12vh, 150px);
  border: 1px solid rgba(184, 151, 88, 0.55);
  border-radius: 10px;
  background: rgba(245, 241, 232, 0.04);
  color: var(--pin-parchment);
  font-family: var(--pin-serif);
  font-size: clamp(1.7rem, 4.6vh, 2.7rem);
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;
  transition: background 90ms ease, transform 90ms ease;
}
.vpin__key.is-down { background: rgba(245, 241, 232, 0.18); transform: scale(0.97); }
.vpin__key:focus-visible { outline: 2px solid var(--pin-gold); outline-offset: 3px; }
.vpin__key--word { font-size: clamp(0.95rem, 2.3vh, 1.35rem); letter-spacing: 0.06em; }
.vpin__key--go { border-color: var(--pin-gold); background: rgba(184, 151, 88, 0.16); }
.vpin__key[disabled] { opacity: 0.32; cursor: default; }
.vpin__foot {
  display: flex;
  gap: clamp(10px, 2vw, 20px);
  flex-wrap: wrap;
  justify-content: center;
  min-height: 1px;
}
.vpin__ghost {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: 1px solid rgba(184, 151, 88, 0.55);
  border-radius: 8px;
  color: var(--pin-parchment);
  font-family: var(--pin-serif);
  font-size: clamp(0.95rem, 2vh, 1.15rem);
  padding: clamp(10px, 1.6vh, 16px) clamp(16px, 3vw, 28px);
  min-height: 48px;
  cursor: pointer;
  touch-action: manipulation;
}
.vpin__ghost--go { border-color: var(--pin-gold); background: rgba(184, 151, 88, 0.2); }
.vpin__ghost:focus-visible { outline: 2px solid var(--pin-gold); outline-offset: 3px; }
.vpin__card {
  border: 1px solid rgba(184, 151, 88, 0.55);
  border-radius: 12px;
  padding: clamp(18px, 3.5vh, 34px) clamp(24px, 6vw, 56px);
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 1.2vh, 12px);
  background: rgba(245, 241, 232, 0.05);
}
.vpin__who { margin: 0; font-size: clamp(1.7rem, 5vh, 3rem); font-weight: 600; line-height: 1.1; }
.vpin__wing { margin: 0; font-size: clamp(1.05rem, 2.6vh, 1.5rem); color: var(--pin-gold); }
.vpin__room { margin: 0; font-size: clamp(0.95rem, 2vh, 1.15rem); opacity: 0.75; }
.vpin--shake { animation: valor-pin-shake 420ms cubic-bezier(0.36, 0.07, 0.19, 0.97); }
@keyframes valor-pin-shake {
  10%, 90% { transform: translateX(-4px); }
  20%, 80% { transform: translateX(8px); }
  30%, 50%, 70% { transform: translateX(-12px); }
  40%, 60% { transform: translateX(12px); }
}
@media (prefers-reduced-motion: reduce) {
  .vpin--shake { animation: none; }
  .vpin__key, .vpin__dot { transition: none; }
}
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

// ------------------------------------------------------------------- helpers

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * appendChild rather than append(): this project compiles the browser and the
 * Worker under one tsconfig, and @cloudflare/workers-types' HTMLRewriter
 * `Element` shadows the DOM's variadic `append`.
 */
function add(parent: HTMLElement, ...children: (HTMLElement | Text)[]): void {
  for (const child of children) parent.appendChild(child);
}

/** Navigate without reaching into main.ts: it already listens for popstate. */
function go(path: string): void {
  history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// --------------------------------------------------------------------- mount

export function mountPinPad(root: HTMLElement, opts: PinPadOptions): () => void {
  ensureStyle();

  const adminMode = opts.mode === 'admin';
  let destroyed = false;
  let phase: Phase = 'checking';
  let entry = '';
  let busy = false;
  let pausedUntil = 0;
  let setupIndex = 0;
  const draft: { admin: string; office: string } = { admin: '', office: '' };
  let welcome: Session | null = null;

  const timers = new Set<number>();
  let holdTimer = 0;
  let hintTimer = 0;
  let countdownTimer = 0;

  const surface = el('section', 'vpin');
  const school = el('p', 'vpin__school', 'Valor Classical Academy');
  const rule = el('hr', 'vpin__rule');
  const title = el('h1', 'vpin__title');
  const hint = el('p', 'vpin__hint');
  const dots = el('div', 'vpin__dots');
  const message = el('p', 'vpin__msg');
  const body = el('div', 'vpin__body');
  const foot = el('footer', 'vpin__foot');

  message.setAttribute('role', 'status');
  message.setAttribute('aria-live', 'polite');
  dots.setAttribute('aria-hidden', 'true');
  add(surface, school, rule, title, hint, dots, message, body, foot);
  root.appendChild(surface);

  // ----------------------------------------------------------- small utils

  function later(fn: () => void, ms: number): number {
    const id = window.setTimeout(() => {
      timers.delete(id);
      if (!destroyed) fn();
    }, ms);
    timers.add(id);
    return id;
  }

  /**
   * Leaving a screen cancels what that screen had queued. Without this, tapping
   * "Not me — sign out" on the welcome card would still be signed in three
   * seconds later by the auto-continue that card had already armed.
   */
  function clearTimers(): void {
    for (const id of timers) window.clearTimeout(id);
    timers.clear();
  }

  /** Must stay synchronous inside a real handler — see the file header. */
  function gesture(): void {
    try {
      opts.onGesture();
    } catch (err) {
      console.warn('[pinpad] audio arm failed', err);
    }
  }

  function setMessage(text: string, kind: 'plain' | 'error' = 'plain'): void {
    message.textContent = text;
    message.classList.toggle('vpin__msg--error', kind === 'error');
  }

  function shake(): void {
    surface.classList.remove('vpin--shake');
    // Force a reflow so the same animation can restart on a second wrong PIN.
    void surface.offsetWidth;
    surface.classList.add('vpin--shake');
  }

  function renderDots(): void {
    dots.replaceChildren();
    for (let i = 0; i < MAX_PIN; i++) {
      const dot = el('span', 'vpin__dot');
      if (i >= MIN_PIN) dot.classList.add('vpin__dot--optional');
      if (i < entry.length) dot.classList.add('vpin__dot--filled');
      dots.appendChild(dot);
    }
  }

  // -------------------------------------------------------------- the keypad

  let keys: HTMLButtonElement[] = [];
  let goKey: HTMLButtonElement | null = null;

  function syncKeys(): void {
    const locked = busy || pausedUntil > Date.now();
    for (const key of keys) key.disabled = locked;
    if (goKey) goKey.disabled = locked || entry.length < MIN_PIN;
  }

  function press(digit: string): void {
    if (busy || pausedUntil > Date.now() || entry.length >= MAX_PIN) return;
    entry += digit;
    renderDots();
    syncKeys();
    if (message.classList.contains('vpin__msg--error')) setMessage('');
  }

  function backspace(): void {
    if (busy || pausedUntil > Date.now()) return;
    entry = entry.slice(0, -1);
    renderDots();
    syncKeys();
  }

  function makeKey(label: string, aria: string, run: () => void, extra = ''): HTMLButtonElement {
    const key = el('button', `vpin__key${extra}`, label);
    key.type = 'button';
    key.setAttribute('aria-label', aria);
    // pointerdown gives the fastest possible arm + press feedback; click is the
    // one that actually enters the digit so mouse, touch and keyboard agree.
    key.addEventListener('pointerdown', () => {
      gesture();
      key.classList.add('is-down');
    });
    const release = () => key.classList.remove('is-down');
    key.addEventListener('pointerup', release);
    key.addEventListener('pointercancel', release);
    key.addEventListener('pointerleave', release);
    key.addEventListener('click', (ev) => {
      ev.preventDefault();
      gesture();
      run();
    });
    return key;
  }

  function buildPad(): HTMLElement {
    const pad = el('div', 'vpin__pad');
    keys = [];
    for (let n = 1; n <= 9; n++) {
      const key = makeKey(String(n), `Digit ${n}`, () => press(String(n)));
      keys.push(key);
      pad.appendChild(key);
    }
    const del = makeKey('⌫', 'Delete last digit', backspace, ' vpin__key--word');
    const zero = makeKey('0', 'Digit 0', () => press('0'));
    goKey = makeKey('Enter', 'Enter PIN', () => void confirmEntry(), ' vpin__key--word vpin__key--go');
    keys.push(del, zero);
    add(pad, del, zero, goKey);
    syncKeys();
    return pad;
  }

  // ------------------------------------------------------------- the pauses

  function startCountdown(seconds: number): void {
    pausedUntil = Date.now() + seconds * 1000;
    syncKeys();
    window.clearInterval(countdownTimer);
    const paint = () => {
      const left = Math.ceil((pausedUntil - Date.now()) / 1000);
      if (left <= 0) {
        window.clearInterval(countdownTimer);
        countdownTimer = 0;
        pausedUntil = 0;
        syncKeys();
        setMessage('You can try your PIN again now.');
        return;
      }
      setMessage(`Too many tries on this tablet. Try again in ${left} second${left === 1 ? '' : 's'}.`, 'error');
    };
    paint();
    countdownTimer = window.setInterval(paint, 250);
  }

  // ------------------------------------------------------------ submitting

  async function confirmEntry(): Promise<void> {
    if (busy || pausedUntil > Date.now() || entry.length < MIN_PIN) return;
    if (phase === 'setup') return void submitSetupStep();
    await submitLogin();
  }

  async function submitLogin(): Promise<void> {
    const pin = entry;
    busy = true;
    syncKeys();
    setMessage('Checking…');

    const result = await submitPin(pin, adminMode ? { requireAdmin: true } : {});
    if (destroyed) return;

    busy = false;
    entry = '';
    renderDots();
    syncKeys();

    if (result.ok && result.session) {
      welcome = result.session;
      phase = 'welcome';
      render();
      return;
    }

    shake();
    setMessage(result.error ?? 'That PIN was not recognised. Try again.', 'error');
    if (result.retryAfter && result.retryAfter > 0) startCountdown(result.retryAfter);
  }

  // ----------------------------------------------------------- first-run setup

  function submitSetupStep(): void {
    const step = SETUP_STEPS[setupIndex];
    if (!step) return;
    const value = entry;
    entry = '';
    renderDots();

    if (!step.confirm) {
      draft[step.which] = value;
      setupIndex += 1;
      render();
      return;
    }

    if (draft[step.which] !== value) {
      draft[step.which] = '';
      setupIndex -= 1;
      shake();
      render();
      setMessage('Those two did not match. Enter that PIN again.', 'error');
      return;
    }

    if (step.which === 'office' && draft.admin === draft.office) {
      draft.office = '';
      setupIndex = 2;
      shake();
      render();
      setMessage('The office PIN must be different from the admin PIN.', 'error');
      return;
    }

    if (setupIndex < SETUP_STEPS.length - 1) {
      setupIndex += 1;
      render();
      return;
    }
    void finishSetup();
  }

  async function finishSetup(): Promise<void> {
    busy = true;
    syncKeys();
    setMessage('Saving…');

    const result = await runSetup(draft.admin, draft.office);
    if (destroyed) return;
    busy = false;

    // Nothing typed here is kept in memory a moment longer than it must be.
    draft.admin = '';
    draft.office = '';

    if (result.ok && result.session) {
      welcome = result.session;
      phase = 'setupDone';
      render();
      return;
    }
    setupIndex = 0;
    phase = 'setup';
    render();
    shake();
    setMessage(result.error ?? 'Setup could not be saved. Try again.', 'error');
  }

  // ---------------------------------------------------------------- rendering

  function finish(session: Session): void {
    if (destroyed) return;
    opts.onSuccess(session);
  }

  function render(): void {
    if (destroyed) return;
    clearTimers();
    body.replaceChildren();
    foot.replaceChildren();
    keys = [];
    goKey = null;
    setMessage('');
    renderDots();
    dots.style.display = '';

    if (phase === 'checking') {
      title.textContent = 'Valor Bells';
      hint.textContent = 'Waking up…';
      dots.style.display = 'none';
      return;
    }

    if (phase === 'offline') {
      title.textContent = 'Valor Bells';
      hint.textContent = '';
      dots.style.display = 'none';
      setMessage('Cannot reach the school server. Check the Wi-Fi, then try again.', 'error');
      const retry = el('button', 'vpin__ghost vpin__ghost--go', 'Try again');
      retry.type = 'button';
      retry.addEventListener('click', () => {
        gesture();
        void begin();
      });
      foot.appendChild(retry);
      return;
    }

    if (phase === 'welcome' && welcome) {
      const session = welcome;
      title.textContent = 'Signed in';
      hint.textContent = 'Check this is the right room before you walk away.';
      dots.style.display = 'none';

      const card = el('div', 'vpin__card');
      add(
        card,
        el('p', 'vpin__who', session.name || session.roomLabel || 'This room'),
        el(
          'p',
          'vpin__wing',
          session.role === 'admin' ? 'Office / Admin' : groupLabel(session),
        ),
      );
      if (session.role !== 'admin' && session.roomLabel && session.roomLabel !== session.name) {
        card.appendChild(el('p', 'vpin__room', session.roomLabel));
      }
      body.appendChild(card);

      const cont = el('button', 'vpin__ghost vpin__ghost--go', 'Continue');
      cont.type = 'button';
      cont.addEventListener('click', () => {
        gesture();
        finish(session);
      });
      const wrong = el('button', 'vpin__ghost', 'Not me — sign out');
      wrong.type = 'button';
      wrong.addEventListener('click', () => {
        gesture();
        clearSession();
        welcome = null;
        phase = 'pin';
        render();
        setMessage('Signed out. Enter the right PIN for this room.');
      });
      add(foot, cont, wrong);
      later(() => finish(session), WELCOME_MS);
      return;
    }

    if (phase === 'setupDone' && welcome) {
      const session = welcome;
      title.textContent = 'PINs are set';
      hint.textContent =
        'Next, give each teacher a PIN in the schedule editor. Their PIN is what tells this tablet which wing to ring.';
      dots.style.display = 'none';

      const open = el('button', 'vpin__ghost vpin__ghost--go', 'Set teacher PINs');
      open.type = 'button';
      open.addEventListener('click', () => {
        gesture();
        history.pushState({}, '', '/admin');
        finish(session);
      });
      const later_ = el('button', 'vpin__ghost', 'Later — go to sign-in');
      later_.type = 'button';
      later_.addEventListener('click', () => {
        gesture();
        // Do not leave an admin session sitting on a classroom wall.
        clearSession();
        welcome = null;
        phase = 'pin';
        render();
      });
      add(foot, open, later_);
      return;
    }

    if (phase === 'setup') {
      const step = SETUP_STEPS[setupIndex] ?? SETUP_STEPS[0]!;
      title.textContent = step.title;
      hint.textContent = step.hint;
      body.appendChild(buildPad());
      return;
    }

    // phase === 'pin'
    title.textContent = adminMode ? 'Office sign-in' : 'Enter your PIN';
    hint.textContent = adminMode
      ? 'Enter the office or admin PIN. A teacher PIN will not open this screen.'
      : 'Your PIN signs this tablet in and starts the bells.';
    body.appendChild(buildPad());

    if (adminMode) {
      const back = el('button', 'vpin__ghost', 'Back to classroom sign-in');
      back.type = 'button';
      back.addEventListener('click', () => {
        gesture();
        go('/');
      });
      foot.appendChild(back);
    }
  }

  // ------------------------------------------------------ hold for the office

  /** The listener that ends the current hold, so a stray press cannot leak one. */
  let endHold: (() => void) | null = null;

  function cancelHold(): void {
    window.clearTimeout(holdTimer);
    window.clearTimeout(hintTimer);
    holdTimer = 0;
    hintTimer = 0;
    if (endHold) {
      surface.removeEventListener('pointerup', endHold);
      surface.removeEventListener('pointercancel', endHold);
      surface.removeEventListener('pointerleave', endHold);
      endHold = null;
    }
  }

  const HOLD_HINT = 'Keep holding for office sign-in…';

  function onHoldStart(): void {
    if (adminMode || phase !== 'pin' || busy) return;
    cancelHold();
    const previous = message.textContent ?? '';
    const wasError = message.classList.contains('vpin__msg--error');
    hintTimer = window.setTimeout(() => {
      if (!destroyed) setMessage(HOLD_HINT);
    }, HOLD_HINT_MS);
    holdTimer = window.setTimeout(() => {
      if (destroyed) return;
      cancelHold();
      go('/admin');
    }, HOLD_MS);
    endHold = () => {
      const hinted = message.textContent === HOLD_HINT;
      cancelHold();
      if (hinted) setMessage(previous, wasError ? 'error' : 'plain');
    };
    surface.addEventListener('pointerup', endHold);
    surface.addEventListener('pointercancel', endHold);
    surface.addEventListener('pointerleave', endHold);
  }

  surface.addEventListener('pointerdown', onHoldStart);

  // A USB or Bluetooth keyboard is a real user gesture too, and it is how the
  // office desk will drive this thing.
  function onKeyDown(ev: KeyboardEvent): void {
    if (destroyed || phase === 'checking' || phase === 'welcome' || phase === 'setupDone') return;
    if (ev.key >= '0' && ev.key <= '9' && ev.key.length === 1) {
      gesture();
      press(ev.key);
    } else if (ev.key === 'Backspace') {
      gesture();
      backspace();
    } else if (ev.key === 'Enter') {
      gesture();
      void confirmEntry();
    } else {
      return;
    }
    ev.preventDefault();
  }
  window.addEventListener('keydown', onKeyDown);

  // ------------------------------------------------------------------- boot

  async function begin(): Promise<void> {
    phase = 'checking';
    render();
    const status = await setupStatus().catch(() => ({ needsSetup: false }));
    if (destroyed) return;
    if (status.needsSetup) {
      phase = 'setup';
      setupIndex = 0;
      draft.admin = '';
      draft.office = '';
    } else {
      phase = 'pin';
    }
    render();
  }

  void begin();

  return () => {
    destroyed = true;
    cancelHold();
    window.clearInterval(countdownTimer);
    clearTimers();
    window.removeEventListener('keydown', onKeyDown);
    surface.removeEventListener('pointerdown', onHoldStart);
    surface.remove();
  };
}
